import {
  FacebookLoginClient,
  InitParams,
  LoginOptions,
  createScriptEle,
  isFacebookApp,
} from '@greatsumini/react-facebook-login';
import * as Sentry from '@sentry/browser';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { isAndroid } from 'react-device-detect';
import { IHandleStartRegistrationProps } from 'types/ui/Registration';
import { authApiClient } from 'utils/apiClient';
import { FACEBOOK_APP_ID } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { keepQueryParameter } from 'utils/url/keepQueryParameter';

interface FacebookCompletionProps {
  accessToken: string;
  userID: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

const initParams: InitParams = {
  appId: FACEBOOK_APP_ID,
  version: 'v9.0',
  xfbml: false,
  cookie: false,
  localStorage: true,
};

export const useFacebookSignIn = (
  handleSuccessfulLogin: (
    token: string,
    name: string,
    abtestVariants: Record<string, string> | undefined | null,
  ) => Promise<void>,
  handleStartRegistration: (
    props: IHandleStartRegistrationProps,
  ) => Promise<void>,
  setError: (error?: string) => void,
) => {
  const router = useRouter();
  const isRedirected = useRef<boolean>(false);

  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const redirect_uri =
    typeof window !== 'undefined'
      ? keepQueryParameter(
          location.origin + location.pathname + location.search,
          'redirect',
        )
      : '/';

  const loginDialogProps = {
    redirect_uri,
    state: 'facebookdirectlogin',
    response_type: 'code',
    client_id: FACEBOOK_APP_ID,
  };

  const registerDialogProps = {
    redirect_uri,
    state: 'facebookdirectregister',
    response_type: 'code',
    client_id: FACEBOOK_APP_ID,
  };

  const loadFB = async () => {
    const language = 'en_US';
    await createScriptEle(
      'facebook-jssdk',
      `https://connect.facebook.net/${language}/all.js`,
    );

    await FacebookLoginClient.loadSdk(language);

    FacebookLoginClient.init(initParams);
  };

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    setTimeout(loadFB, 500);
  }, [router.isReady]);

  const completeSignIn = async (response: {
    accessToken: string;
    userID: string;
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    if (!response.accessToken || !response.userID) {
      Sentry.captureMessage(
        `Facebook login failed no response.accessToken ${response.accessToken} or response.userID ${response.userID}`,
      );
      return;
    }

    const loginResponse = await authApiClient.public.loginWithFacebook({
      body: {
        accessToken: response.accessToken,
      },
    });

    if (loginResponse.status === HttpStatusCode.OK) {
      await handleSuccessfulLogin(
        loginResponse.body.token,
        loginResponse.body.welcomeName,
        loginResponse.body.abtestVariants,
      );
    } else {
      Sentry.captureMessage(
        `loginResponse not successful ${loginResponse.body.message}`,
      );

      setError(loginResponse.body.message);
    }
  };

  const completeRegistration = async (response: {
    accessToken: string;
    userID: string;
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    if (!response.accessToken || !response.userID) {
      Sentry.captureMessage(
        `Facebook login failed no response.accessToken ${response.accessToken} or response.userID ${response.userID}`,
      );
      return;
    }

    const registrationResult =
      await authApiClient.public.validateFacebookAccessToken({
        body: {
          accessToken: response.accessToken,
          userId: response.userID,
          email: response.email,
        },
      });

    if (registrationResult.status === HttpStatusCode.BAD_REQUEST) {
      const errorString = registrationResult.body.message;
      setError(errorString);
      return;
    } else if (registrationResult.status === HttpStatusCode.OK) {
      handleSuccessfulLogin(
        registrationResult.body.token,
        registrationResult.body.welcomeName,
        registrationResult.body.abtestVariants,
      );
      return;
    }

    handleStartRegistration({
      facebookUserId: response.userID,
      email: response.email ?? '',
      firstName: response.first_name ?? '',
      lastName: response.last_name ?? '',
    });
  };

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const isLoginRedirected =
      FacebookLoginClient.isRedirected(loginDialogProps);
    const isRegisterRedirected =
      FacebookLoginClient.isRedirected(registerDialogProps);

    isRedirected.current = isLoginRedirected || isRegisterRedirected;
    if (isRedirected.current) {
      setIsFacebookLoading(true);
      const loadProfile = async () => {
        try {
          const codeQueryStringParam = router.query.code;
          const code = Array.isArray(codeQueryStringParam)
            ? codeQueryStringParam[0]
            : codeQueryStringParam;
          if (!code) {
            console.error(
              'Failed to log in with facebook - ' + window.location.href,
            );
            setError(
              'Sorry, there was a problem signing in with Facebook - Facebook did not return a log in code',
            );
            return;
          }
          const redirect_uri = isLoginRedirected
            ? loginDialogProps.redirect_uri
            : registerDialogProps.redirect_uri;

          const profileResponse =
            await authApiClient.public.getFacebookProfileFromCode({
              body: {
                code,
                redirect_uri,
              },
            });

          if (profileResponse.status == HttpStatusCode.OK) {
            if (isLoginRedirected) {
              completeSignIn({
                accessToken: profileResponse.body.accessToken,
                userID: profileResponse.body.id,
                email: profileResponse.body.email,
                first_name: profileResponse.body.firstName,
                last_name: profileResponse.body.lastName,
              });
            } else {
              completeRegistration({
                accessToken: profileResponse.body.accessToken,
                userID: profileResponse.body.id,
                email: profileResponse.body.email,
                first_name: profileResponse.body.firstName,
                last_name: profileResponse.body.lastName,
              });
            }
          } else {
            setError(
              'Sorry, there was a problem completing signing in with Facebook',
            );
            console.log(
              'Facebook redirect login failed: ',
              profileResponse.body.message,
            );
          }
        } finally {
          setIsFacebookLoading(false);
        }
      };

      loadProfile();
    }
  }, [router.isReady]);

  const doFacebookSignIn = () => {
    const loginOptions: LoginOptions = {
      scope: 'public_profile,email',
    };
    setIsFacebookLoading(true);

    if (isFacebookApp() || isAndroid) {
      FacebookLoginClient.redirectToDialog(loginDialogProps, loginOptions);
    } else {
      FacebookLoginClient.login((loginResponse) => {
        if (isRedirected.current) {
          console.log('Facebook login onSucccess aborted: redirected');
          return;
        }

        if (!loginResponse.authResponse) {
          console.log('Login Failed!', loginResponse.status);
          setIsFacebookLoading(false);

          return;
        }

        const authResponse = loginResponse.authResponse;

        FacebookLoginClient.getProfile(
          (res: any) => {
            setIsFacebookLoading(false);

            completeSignIn({
              accessToken: authResponse.accessToken,
              userID: authResponse.userID,
              email: res.email,
              first_name: res.first_name,
              last_name: res.last_name,
            });
          },
          { fields: 'id,email,first_name,last_name' },
        );
      }, loginOptions);
    }
  };

  const doFacebookRegister = () => {
    const loginOptions: LoginOptions = {
      scope: 'public_profile,email',
    };

    setIsFacebookLoading(true);

    if (isFacebookApp() || isAndroid) {
      FacebookLoginClient.redirectToDialog(registerDialogProps, loginOptions);
    } else {
      FacebookLoginClient.login((loginResponse) => {
        if (isRedirected.current) {
          console.log('Facebook login onSucccess aborted: redirected');
          return;
        }

        if (!loginResponse.authResponse) {
          setError('Facebook login failed');
          setIsFacebookLoading(false);
          Sentry.captureMessage(
            `Facebook login failed no authResponse ${loginResponse.status}`,
          );
          console.log('Login Failed!', loginResponse.status);
          return;
        }

        const authResponse = loginResponse.authResponse;

        FacebookLoginClient.getProfile(
          (res: any) => {
            setIsFacebookLoading(false);
            completeRegistration({
              accessToken: authResponse.accessToken,
              userID: authResponse.userID,
              email: res.email,
              first_name: res.first_name,
              last_name: res.last_name,
            });
          },
          { fields: 'id,email,first_name,last_name' },
        );
      }, loginOptions);
    }
  };

  return {
    isFacebookLoading,
    doFacebookSignIn,
    doFacebookRegister,
  };
};
